import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import { ArrowLeft, Loader2, X } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import {
  NIDLP_SURVEY_COPY,
  NIDLP_SURVEY_OPTION_COLUMNS,
  NIDLP_SURVEY_OPTIONS,
  t,
  type SurveyLanguage,
  type SurveyOption,
} from '@/data/nidlpSurveyContent'
import { NIDLP_SURVEY_ASSETS } from '@/data/nidlpSurveyAssets'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventTheme {
  id: number
  name: string
  bannerUrl: string | null
  logoUrl: string | null
}

export interface NidlpSurveyProps {
  event: EventTheme
  urls: {
    submit: string
    register: string
  }
  investmentUrl: string
  submitted?: boolean
  showQr?: boolean
  qrCodeDataUrl?: string | null
  language?: SurveyLanguage | null
  errors?: string[]
}

type Screen = 'welcome' | 'survey'

const BUTTON_ASPECT = 519 / 308
const SUCCESS_REDIRECT_MS = 30_000

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function NidlpSurvey({
  event,
  urls,
  submitted = false,
  showQr = false,
  language: initialLanguage = null,
  errors = [],
}: NidlpSurveyProps) {
  const [screen, setScreen] = useState<Screen>('welcome')
  const [language, setLanguage] = useState<SurveyLanguage>('en')
  const [selections, setSelections] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [successOpen, setSuccessOpen] = useState(false)

  const resetToWelcome = useCallback(() => {
    setSuccessOpen(false)
    setScreen('welcome')
    setSelections([])
    setValidationError(null)
    router.get(urls.register, {}, { preserveState: false, replace: true })
  }, [urls.register])

  useEffect(() => {
    if (submitted) {
      if (initialLanguage) setLanguage(initialLanguage)
      setSuccessOpen(true)
    }
  }, [submitted, initialLanguage])

  useEffect(() => {
    if (!successOpen) return

    const timer = window.setTimeout(resetToWelcome, SUCCESS_REDIRECT_MS)
    return () => window.clearTimeout(timer)
  }, [successOpen, resetToWelcome])

  const startSurvey = (lang: SurveyLanguage) => {
    setLanguage(lang)
    setSelections([])
    setValidationError(null)
    setScreen('survey')
  }

  const toggleSelection = (id: string) => {
    setSelections(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    )
    setValidationError(null)
  }

  const submitSurvey = (submitType: 'survey_only' | 'with_investment') => {
    if (selections.length === 0) {
      setValidationError(t(NIDLP_SURVEY_COPY.selectAtLeastOne, language))
      return
    }

    setSubmitting(true)
    router.post(
      urls.submit,
      { language, selections, submit_type: submitType },
      { preserveState: true, onFinish: () => setSubmitting(false) },
    )
  }

  const handleSubmit = (e: FormEvent, submitType: 'survey_only' | 'with_investment') => {
    e.preventDefault()
    submitSurvey(submitType)
  }

  return (
    <>
      <Head title={`${event.name} – Survey`} />

      <div className="relative flex min-h-dvh flex-col overflow-hidden bg-black text-[#FFF7EB]">
        <img
          src={NIDLP_SURVEY_ASSETS.earth}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />

        <SurveyHeader bannerUrl={event.bannerUrl} />

        <main className="relative z-10 flex flex-1 flex-col">
          {screen === 'welcome' ? (
            <WelcomeScreen onStart={startSurvey} />
          ) : (
            <SurveyScreen
              language={language}
              selections={selections}
              submitting={submitting}
              validationError={validationError}
              serverErrors={errors}
              onBack={() => setScreen('welcome')}
              onClose={resetToWelcome}
              onToggle={toggleSelection}
              onSubmit={handleSubmit}
            />
          )}
        </main>

        {successOpen && (
          <SuccessModal language={language} showQr={showQr} onClose={resetToWelcome} />
        )}
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Header                                                             */
/* ------------------------------------------------------------------ */

function SurveyHeader({ bannerUrl }: { bannerUrl: string | null }) {
  return (
    <header className="relative z-20 shrink-0">
      {bannerUrl ? (
        <img src={bannerUrl} alt="Saudi Pavilion" className="block w-full" />
      ) : (
        <>
          <div className="flex items-center justify-center gap-8 px-6 py-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a1a1a] sm:text-sm">
            <span>Vision 2030</span>
            <span>NIDLP</span>
            <span>Invest Saudi</span>
          </div>
          <SawtoothEdge />
        </>
      )}
    </header>
  )
}

function SawtoothEdge() {
  return (
    <div
      className="h-3 w-full bg-white"
      style={{
        clipPath:
          'polygon(0 0, 2% 100%, 4% 0, 6% 100%, 8% 0, 10% 100%, 12% 0, 14% 100%, 16% 0, 18% 100%, 20% 0, 22% 100%, 24% 0, 26% 100%, 28% 0, 30% 100%, 32% 0, 34% 100%, 36% 0, 38% 100%, 40% 0, 42% 100%, 44% 0, 46% 100%, 48% 0, 50% 100%, 52% 0, 54% 100%, 56% 0, 58% 100%, 60% 0, 62% 100%, 64% 0, 66% 100%, 68% 0, 70% 100%, 72% 0, 74% 100%, 76% 0, 78% 100%, 80% 0, 82% 100%, 84% 0, 86% 100%, 88% 0, 90% 100%, 92% 0, 94% 100%, 96% 0, 98% 100%, 100% 0)',
      }}
    />
  )
}

/* ------------------------------------------------------------------ */
/*  Welcome                                                            */
/* ------------------------------------------------------------------ */

function WelcomeScreen({ onStart }: { onStart: (lang: SurveyLanguage) => void }) {
  return (
    <div className="mx-auto flex w-full max-w-[1120px] flex-1 flex-col px-6 pb-8 pt-6 sm:px-10 sm:pb-10">
      <div className="flex flex-1 flex-col items-center justify-center">
        <img
          src={NIDLP_SURVEY_ASSETS.welcome}
          alt="Welcome"
          className="mb-8 w-full max-w-[640px]"
        />

        <p className="mb-10 max-w-[860px] text-center text-sm leading-relaxed text-[#FFF7EB]/90 sm:text-base">
          {t(NIDLP_SURVEY_COPY.welcomeSubtitle, 'en')}
          <span className="mx-3 text-[#FFF7EB]/35">|</span>
          {t(NIDLP_SURVEY_COPY.welcomeSubtitle, 'zh')}
        </p>

        <div className="grid w-full max-w-[860px] grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
          <WelcomeLanguageButton
            label={t(NIDLP_SURVEY_COPY.startSurvey, 'en')}
            onClick={() => onStart('en')}
          />
          <WelcomeLanguageButton
            label={t(NIDLP_SURVEY_COPY.startSurvey, 'zh')}
            onClick={() => onStart('zh')}
          />
        </div>
      </div>

      <p className="mt-8 text-center text-xs tracking-[0.1em] text-[#FFF7EB]/70 sm:text-sm">
        {t(NIDLP_SURVEY_COPY.footer, 'en')}
        <span className="mx-3 text-[#FFF7EB]/30">|</span>
        {t(NIDLP_SURVEY_COPY.footer, 'zh')}
      </p>
    </div>
  )
}

function WelcomeLanguageButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative w-full overflow-hidden transition hover:brightness-110"
      style={{ aspectRatio: String(BUTTON_ASPECT) }}
    >
      <img
        src={NIDLP_SURVEY_ASSETS.button}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-fill"
      />
      <span className="relative flex h-full items-center justify-center px-6 text-lg font-medium text-[#FFF7EB] sm:text-xl">
        {label}
      </span>
    </button>
  )
}

/* ------------------------------------------------------------------ */
/*  Survey                                                             */
/* ------------------------------------------------------------------ */

interface SurveyScreenProps {
  language: SurveyLanguage
  selections: string[]
  submitting: boolean
  validationError: string | null
  serverErrors: string[]
  onBack: () => void
  onClose: () => void
  onToggle: (id: string) => void
  onSubmit: (e: FormEvent, submitType: 'survey_only' | 'with_investment') => void
}

function SurveyScreen({
  language,
  selections,
  submitting,
  validationError,
  serverErrors,
  onBack,
  onClose,
  onToggle,
  onSubmit,
}: SurveyScreenProps) {
  const optionsById = Object.fromEntries(NIDLP_SURVEY_OPTIONS.map(option => [option.id, option]))
  const leftColumnOptions = NIDLP_SURVEY_OPTION_COLUMNS.left.map(id => optionsById[id]).filter(Boolean)
  const rightColumnOptions = NIDLP_SURVEY_OPTION_COLUMNS.right.map(id => optionsById[id]).filter(Boolean)

  return (
    <div className="mx-auto flex w-full max-w-[1120px] flex-1 flex-col px-6 py-6 sm:px-10 sm:py-8">
      <div className="mb-5 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="p-1 text-[#FFF7EB]/85 transition hover:text-[#FFF7EB]"
          aria-label="Back"
        >
          <ArrowLeft className="h-6 w-6" strokeWidth={1.5} />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-[#FFF7EB]/85 transition hover:text-[#FFF7EB]"
          aria-label="Close"
        >
          <X className="h-6 w-6" strokeWidth={1.5} />
        </button>
      </div>

      <div className="flex-1 border border-[#FFF7EB]/25 bg-black/30 px-5 py-6 backdrop-blur-sm sm:px-8 sm:py-8">
        <div className="mb-5 flex items-start gap-4 sm:mb-6 sm:gap-5">
          <img
            src={NIDLP_SURVEY_ASSETS.clipboard}
            alt=""
            aria-hidden
            className="mt-1 h-14 w-14 shrink-0 sm:h-16 sm:w-16"
          />
          <h2 className="text-base font-normal leading-relaxed text-[#FFF7EB] sm:text-xl">
            {t(NIDLP_SURVEY_COPY.question, language)}
          </h2>
        </div>

        <div className="mb-5 h-px w-full bg-[#FFF7EB]/25 sm:mb-6" />

        <div className="flex flex-col sm:flex-row sm:gap-0">
          <SurveyOptionColumn
            language={language}
            options={leftColumnOptions}
            selections={selections}
            onToggle={onToggle}
          />
          <div className="my-4 hidden w-px shrink-0 self-stretch bg-[#FFF7EB]/25 sm:my-0 sm:block" />
          <div className="h-px w-full bg-[#FFF7EB]/25 sm:hidden" />
          <SurveyOptionColumn
            language={language}
            options={rightColumnOptions}
            selections={selections}
            onToggle={onToggle}
          />
        </div>

        {(validationError || serverErrors.length > 0) && (
          <div className="mt-6 border border-red-400/30 bg-red-950/40 px-4 py-3 text-sm text-red-100">
            {validationError || serverErrors.join(', ')}
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
        <SurveySubmitButton
          disabled={submitting}
          variant="primary"
          onClick={e => onSubmit(e, 'survey_only')}
          label={t(NIDLP_SURVEY_COPY.submitSurvey, language)}
        />
        <SurveySubmitButton
          disabled={submitting}
          variant="accent"
          onClick={e => onSubmit(e, 'with_investment')}
          label={t(NIDLP_SURVEY_COPY.submitAndExplore, language)}
        />
      </div>
    </div>
  )
}

function SurveyOptionColumn({
  language,
  options,
  selections,
  onToggle,
}: {
  language: SurveyLanguage
  options: SurveyOption[]
  selections: string[]
  onToggle: (id: string) => void
}) {
  return (
    <div className="flex flex-1 flex-col gap-3 sm:gap-4 sm:px-6 sm:first:pl-0 sm:last:pr-0">
      {options.map(option => {
        const checked = selections.includes(option.id)
        const label = language === 'zh' ? option.zh : option.en

        return (
          <label
            key={option.id}
            className="flex cursor-pointer items-start gap-3 py-1"
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => onToggle(option.id)}
              className="mt-0.5 size-4 rounded-none border-[#FFF7EB]/70 data-[state=checked]:border-[#FFF7EB] data-[state=checked]:bg-[#FFF7EB] data-[state=checked]:text-black"
            />
            <span className="text-sm leading-relaxed text-[#FFF7EB]/95 sm:text-base">{label}</span>
          </label>
        )
      })}
    </div>
  )
}

function SurveySubmitButton({
  label,
  onClick,
  disabled,
  variant,
}: {
  label: string
  onClick: (e: FormEvent) => void
  disabled: boolean
  variant: 'primary' | 'accent'
}) {
  const gradient =
    variant === 'accent'
      ? 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(30,60,45,0.75) 50%, rgba(0,0,0,0.55) 100%)'
      : 'linear-gradient(180deg, rgba(255,247,235,0.14) 0%, rgba(255,247,235,0.04) 100%)'

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="min-h-[56px] border border-[#FFF7EB]/25 px-4 py-4 text-sm font-medium text-[#FFF7EB] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-[64px] sm:text-base"
      style={{ background: gradient }}
    >
      {disabled ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : label}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/*  Success modal                                                      */
/* ------------------------------------------------------------------ */

function SuccessModal({
  language,
  showQr,
  onClose,
}: {
  language: SurveyLanguage
  showQr: boolean
  onClose: () => void
}) {
  const qrAsset = language === 'zh' ? NIDLP_SURVEY_ASSETS.qrCn : NIDLP_SURVEY_ASSETS.qrEn

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6 backdrop-blur-[2px]">
      <div className="relative w-full max-w-[360px] border border-[#FFF7EB]/25 bg-black/70 px-8 py-10 text-center">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-[#FFF7EB]/70 transition hover:text-[#FFF7EB]"
          aria-label="Close"
        >
          <X className="h-5 w-5" strokeWidth={1.5} />
        </button>

        <img
          src={NIDLP_SURVEY_ASSETS.checkmark}
          alt=""
          aria-hidden
          className="mx-auto mb-6 h-[88px] w-[88px]"
        />

        <p className="text-lg leading-relaxed text-[#FFF7EB] sm:text-xl">
          {t(NIDLP_SURVEY_COPY.successTitle, language)}
        </p>

        {showQr && (
          <img
            src={qrAsset}
            alt={t(NIDLP_SURVEY_COPY.scanQr, language)}
            className="mx-auto mt-8 w-full max-w-[220px]"
          />
        )}
      </div>
    </div>
  )
}
